import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { MessageSquarePlus, X, Camera, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE;
const ADMIN_EMAIL = 'rohit@eipimedia.com';
const STORAGE_KEY = 'feedback-widget-pos';

const getInitialPos = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved?.x != null && saved?.y != null) return saved;
  } catch {}
  // Default to the bottom-left CORNER so the bubble never sits on top of
  // mid-page form content (especially on 9:16 mobile where Stage 1 buttons
  // and hint text were getting hidden under the widget). User can drag.
  return { x: 8, y: typeof window !== 'undefined' ? window.innerHeight - 56 : 700 };
};

export default function FeedbackWidget({ userEmail }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [pos, setPos] = useState(getInitialPos);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const fileRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      setScreenshot(e.target.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          handleFile(item.getAsFile());
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, handleFile]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    movedRef.current = false;
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const size = 44;
    const onMove = (e) => {
      movedRef.current = true;
      const nx = Math.max(0, Math.min(window.innerWidth - size, e.clientX - offsetRef.current.x));
      const ny = Math.max(0, Math.min(window.innerHeight - size, e.clientY - offsetRef.current.y));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      setDragging(false);
      setPos(p => { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); return p; });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging]);

  const handleClick = useCallback(() => {
    if (!movedRef.current) setOpen(true);
  }, []);

  const submit = useCallback(async () => {
    if (!comment.trim()) { toast.error('Add a comment'); return; }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/feedback`, {
        comment: comment.trim(),
        page_url: window.location.href,
        screenshot_b64: screenshot,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
      setTimeout(() => { setOpen(false); setSent(false); setComment(''); setScreenshot(null); setPreview(null); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSending(false);
    }
  }, [comment, screenshot]);

  // Early return AFTER all hooks
  if (userEmail !== ADMIN_EMAIL) return null;

  return (
    <>
      {!open && (
        <div
          ref={dragRef}
          onPointerDown={onPointerDown}
          onClick={handleClick}
          className="z-[60] w-11 h-11 rounded-full flex items-center justify-center shadow-lg select-none touch-none"
          style={{
            position: 'fixed',
            left: pos.x,
            top: pos.y,
            background: '#0EA5E9',
            cursor: dragging ? 'grabbing' : 'grab',
            transition: dragging ? 'none' : 'box-shadow 0.2s',
            boxShadow: dragging ? '0 8px 25px rgba(14,165,233,0.4)' : '0 4px 15px rgba(14,165,233,0.3)',
          }}
          data-testid="feedback-trigger"
        >
          <MessageSquarePlus className="w-5 h-5 text-white pointer-events-none" />
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md rounded-xl overflow-hidden"
            style={{ background: '#0B0F19', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={e => e.stopPropagation()}
            data-testid="feedback-modal"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <span className="text-sm font-medium text-white">Bug Report / Feedback</span>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/5 rounded-lg">
                <X className="w-4 h-4 text-white/40" />
              </button>
            </div>
            {sent ? (
              <div className="p-8 flex flex-col items-center gap-3">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" />
                <p className="text-sm text-white/70">Submitted — will be picked up next session</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="px-3 py-2 rounded-lg text-[10px] text-white/30 font-mono truncate" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  {window.location.pathname}
                </div>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Describe the issue... (Ctrl+V to paste screenshot)"
                  className="w-full h-24 px-3 py-2 rounded-lg text-sm text-white placeholder-white/20 resize-none focus:outline-none focus:ring-1 focus:ring-[#0EA5E9]/50"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                  autoFocus
                  data-testid="feedback-comment"
                />
                {preview && (
                  <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                    <img src={preview} alt="Screenshot" className="w-full max-h-40 object-contain bg-black/40" />
                    <button onClick={() => { setScreenshot(null); setPreview(null); }} className="absolute top-1 right-1 p-1 rounded-full bg-black/60">
                      <X className="w-3 h-3 text-white/60" />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors">
                    <Camera className="w-3.5 h-3.5" />
                    {preview ? 'Replace' : 'Screenshot'}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
                  <button
                    onClick={submit}
                    disabled={sending || !comment.trim()}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium text-white transition-all disabled:opacity-30"
                    style={{ background: '#0EA5E9' }}
                    data-testid="feedback-submit"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Submit
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
